import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Shield, ArrowLeft, KeyRound, Mail, Check, X as XIcon, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNoIndex } from "@/hooks/use-no-index";

// ---------- password-strength meter (client-side hint only; server enforces) ----------
// We evaluate 5 rules; each satisfied rule = 20% strength. Below 100% = the
// server will reject anyway, but this saves a network round-trip and gives
// the admin instant feedback while typing.
function evaluateStrength(pw) {
  const checks = [
    { key: "len",    label: "At least 12 characters", ok: (pw || "").length >= 12 },
    { key: "lower",  label: "Lowercase letter",       ok: /[a-z]/.test(pw || "") },
    { key: "upper",  label: "Uppercase letter",       ok: /[A-Z]/.test(pw || "") },
    { key: "digit",  label: "Number",                 ok: /[0-9]/.test(pw || "") },
    { key: "symbol", label: "Symbol (!@#$ …)",        ok: /[^A-Za-z0-9]/.test(pw || "") },
  ];
  const score = checks.filter((c) => c.ok).length;
  return { checks, score, pct: (score / checks.length) * 100 };
}

export default function AdminLogin() {
  const { setUser } = useAuth();
  const nav = useNavigate();

  // Three-step state machine:
  //   'password'  → email + password entry
  //   '2fa'       → 6-digit OTP entry after successful password
  //   'rotate'    → forced password change for accounts on default creds
  const [step, setStep] = useState("password");
  const [loading, setLoading] = useState(false);

  const [f, setF] = useState({ email: "", password: "" });
  const [twoFA, setTwoFA] = useState({ challenge_id: "", code: "", devHint: "" });
  const [rotate, setRotate] = useState({ pw_change_token: "", new_password: "", confirm: "", show: false });

  const strength = useMemo(() => evaluateStrength(rotate.new_password), [rotate.new_password]);

  useEffect(() => {
    document.title = "Admin Login - Pune Spa Guide";
  }, []);
  useNoIndex();

  // -------- Step 1: password submit --------
  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email: f.email.trim(), password: f.password });
      const data = res.data;
      if (data.must_change_password) {
        // Layer 1: default/rotation-flagged account. Hand off to rotation step.
        setRotate((r) => ({ ...r, pw_change_token: data.pw_change_token }));
        setStep("rotate");
        toast.message("Password change required", {
          description: "Your account is using a default password. Please set a strong new password to continue.",
        });
        return;
      }
      if (data.requires_2fa) {
        // Layer 3: admin flow. Prompt for the 6-digit code.
        setTwoFA({ challenge_id: data.challenge_id, code: "", devHint: data.dev_code || "" });
        setStep("2fa");
        return;
      }
      // Non-admin (owner). Shouldn't reach the admin login page, but handle gracefully.
      if (data.user?.role !== "admin") {
        toast.error("Not an admin account");
        return;
      }
      localStorage.setItem("psg_user", JSON.stringify(data.user));
      setUser(data.user);
      toast.success("Admin access granted");
      nav("/admin");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Login failed";
      // Explicit 429 message helps admins understand this isn't a bug — it's the lockout kicking in.
      if (status === 429) {
        toast.error(detail, { description: "Wait a few minutes before retrying to avoid a longer lockout." });
      } else {
        toast.error(detail);
      }
    } finally { setLoading(false); }
  };

  // -------- Step 2: 2FA submit --------
  const submit2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login/verify-2fa", {
        challenge_id: twoFA.challenge_id,
        code: twoFA.code.trim(),
      });
      const data = res.data;
      localStorage.setItem("psg_user", JSON.stringify(data.user));
      setUser(data.user);
      toast.success("Admin access granted");
      nav("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Verification failed";
      toast.error(detail);
      // If the challenge was invalidated, bounce back to password entry so
      // the admin can request a fresh code cleanly.
      if (/already used|expired|log in again/i.test(detail)) {
        setStep("password");
        setTwoFA({ challenge_id: "", code: "", devHint: "" });
      }
    } finally { setLoading(false); }
  };

  // -------- Step 3: force password change --------
  const submitRotate = async (e) => {
    e.preventDefault();
    if (rotate.new_password !== rotate.confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    if (strength.score < 5) {
      toast.error("Password doesn't meet all requirements.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/force-password-change", {
        pw_change_token: rotate.pw_change_token,
        new_password: rotate.new_password,
      });
      // Backend sets the session cookie AND returns the user — treat this as a full login.
      const user = res.data.user;
      localStorage.setItem("psg_user", JSON.stringify(user));
      setUser(user);
      toast.success("Password updated. Welcome back!");
      nav("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="admin-login-page" className="max-w-md mx-auto px-4 py-16">
      <div className="rounded-2xl bg-[#111827] text-white p-8">
        <div className="flex items-center justify-between mb-3">
          <Shield size={24} className="text-[#C9A86A]" aria-hidden />
          {step !== "password" && (
            <button
              type="button"
              onClick={() => { setStep("password"); setTwoFA({ challenge_id: "", code: "", devHint: "" }); }}
              className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1"
              data-testid="admin-login-back-btn"
            >
              <ArrowLeft size={12}/> Start over
            </button>
          )}
        </div>
        <h1 className="font-heading text-3xl">Admin Login</h1>
        <p className="text-sm text-white/70 mt-1">Restricted access. Authorised personnel only.</p>

        {/* ---------- Step 1: Password ---------- */}
        {step === "password" && (
          <form onSubmit={submitPassword} className="mt-6 space-y-4" data-testid="admin-login-step-password">
            <div>
              <Label className="text-xs text-white/80">Email</Label>
              <Input
                type="email"
                autoComplete="username"
                value={f.email}
                onChange={(e) => setF({ ...f, email: e.target.value })}
                required
                data-testid="admin-login-email-input"
                className="bg-white text-[#111827]"
              />
            </div>
            <div>
              <Label className="text-xs text-white/80">Password</Label>
              <Input
                type="password"
                autoComplete="current-password"
                value={f.password}
                onChange={(e) => setF({ ...f, password: e.target.value })}
                required
                data-testid="admin-login-password-input"
                className="bg-white text-[#111827]"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full h-11 bg-[#C9A86A] hover:bg-[#D7BC86] text-[#111827]"
            >
              {loading ? "Authenticating..." : "Continue"}
            </Button>
          </form>
        )}

        {/* ---------- Step 2: 2FA ---------- */}
        {step === "2fa" && (
          <form onSubmit={submit2FA} className="mt-6 space-y-4" data-testid="admin-login-step-2fa">
            <div className="rounded-lg bg-white/5 border border-white/10 p-3 flex items-start gap-2">
              <Mail size={16} className="text-[#C9A86A] mt-0.5 shrink-0"/>
              <div className="text-xs text-white/80">
                A 6-digit verification code was sent to your admin email. Enter it below to complete sign-in.
                <div className="text-white/50 mt-1">Code expires in 10 minutes.</div>
              </div>
            </div>
            <div>
              <Label className="text-xs text-white/80">Verification code</Label>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
                value={twoFA.code}
                onChange={(e) => setTwoFA({ ...twoFA, code: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                required
                data-testid="admin-login-otp-input"
                className="bg-white text-[#111827] text-center text-2xl tracking-[0.5em] font-mono"
                placeholder="••••••"
                autoFocus
              />
              {twoFA.devHint && (
                <div className="mt-2 text-[11px] text-amber-300/90" data-testid="admin-login-dev-hint">
                  Dev/preview only — code: <span className="font-mono font-medium">{twoFA.devHint}</span>
                </div>
              )}
            </div>
            <Button
              type="submit"
              disabled={loading || twoFA.code.length !== 6}
              data-testid="admin-login-verify-2fa-btn"
              className="w-full h-11 bg-[#C9A86A] hover:bg-[#D7BC86] text-[#111827]"
            >
              {loading ? "Verifying..." : "Verify & Sign in"}
            </Button>
          </form>
        )}

        {/* ---------- Step 3: Force password rotation ---------- */}
        {step === "rotate" && (
          <form onSubmit={submitRotate} className="mt-6 space-y-4" data-testid="admin-login-step-rotate">
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 flex items-start gap-2">
              <KeyRound size={16} className="text-amber-300 mt-0.5 shrink-0"/>
              <div className="text-xs text-amber-100">
                <strong>One-time password rotation required.</strong> Your account is on a default or weak password. Choose a strong one to continue.
              </div>
            </div>
            <div>
              <Label className="text-xs text-white/80">New password</Label>
              <div className="relative">
                <Input
                  type={rotate.show ? "text" : "password"}
                  autoComplete="new-password"
                  value={rotate.new_password}
                  onChange={(e) => setRotate({ ...rotate, new_password: e.target.value })}
                  required
                  data-testid="admin-rotate-newpw-input"
                  className="bg-white text-[#111827] pr-10"
                />
                <button
                  type="button"
                  onClick={() => setRotate({ ...rotate, show: !rotate.show })}
                  className="absolute inset-y-0 right-2 flex items-center text-[#4B5563] hover:text-[#111827]"
                  data-testid="admin-rotate-toggle-visibility"
                  aria-label={rotate.show ? "Hide password" : "Show password"}
                >
                  {rotate.show ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
              {/* strength meter */}
              <div className="mt-2 h-1.5 rounded bg-white/10 overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${strength.pct}%`,
                    backgroundColor: strength.score >= 5 ? "#22c55e" : strength.score >= 3 ? "#eab308" : "#ef4444",
                  }}
                  data-testid="admin-rotate-strength-bar"
                />
              </div>
              <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]" data-testid="admin-rotate-checks">
                {strength.checks.map((c) => (
                  <li key={c.key} className={c.ok ? "text-emerald-300" : "text-white/60"}>
                    {c.ok ? <Check size={11} className="inline mr-1"/> : <XIcon size={11} className="inline mr-1"/>}
                    {c.label}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <Label className="text-xs text-white/80">Confirm new password</Label>
              <Input
                type={rotate.show ? "text" : "password"}
                autoComplete="new-password"
                value={rotate.confirm}
                onChange={(e) => setRotate({ ...rotate, confirm: e.target.value })}
                required
                data-testid="admin-rotate-confirm-input"
                className="bg-white text-[#111827]"
              />
              {rotate.confirm && rotate.confirm !== rotate.new_password && (
                <div className="text-[11px] text-red-300 mt-1">Passwords don&apos;t match.</div>
              )}
            </div>
            <Button
              type="submit"
              disabled={loading || strength.score < 5 || rotate.new_password !== rotate.confirm}
              data-testid="admin-rotate-submit"
              className="w-full h-11 bg-[#C9A86A] hover:bg-[#D7BC86] text-[#111827]"
            >
              {loading ? "Updating…" : "Set new password & sign in"}
            </Button>
          </form>
        )}

        <div className="mt-4 text-xs text-white/60 text-center">
          <Link to="/login">← Spa Owner Login</Link>
        </div>
      </div>
    </div>
  );
}
